import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Download, Trash2, Clock, CheckCircle, AlertTriangle, Zap, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function DataRequestPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/legal/data-request/mine');
      setRequests(res.data.requests || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const submit = async (type) => {
    setSubmitting(type);
    try {
      await api.post('/legal/data-request', { type });
      toast.success(`${type === 'export' ? 'Data export' : 'Account deletion'} request submitted`);
      load();
      setConfirmDelete(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    }
    setSubmitting(null);
  };

  const hasPending = (type) => requests.some(r => r.type === type && r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto h-full px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-500" />
            <span className="font-black text-lg tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-slate-500">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-black mb-1 text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>Your Data Rights</h1>
        <p className="text-sm text-slate-500 mb-8">
          Under UK GDPR, you have the right to request a copy of your data or the deletion of your account.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 mb-10">
          {/* Data Export */}
          <Card className="border-slate-200" data-testid="data-export-card">
            <CardContent className="pt-6 pb-5 text-center space-y-3">
              <Download className="w-8 h-8 text-blue-500 mx-auto" />
              <h3 className="font-bold text-slate-900">Data Export</h3>
              <p className="text-xs text-slate-500">Request a copy of all personal data we hold about you.</p>
              <Button
                size="sm"
                onClick={() => submit('export')}
                disabled={submitting === 'export' || hasPending('export')}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                data-testid="request-export-btn"
              >
                {submitting === 'export' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {hasPending('export') ? 'Request Pending' : 'Request Export'}
              </Button>
            </CardContent>
          </Card>

          {/* Account Deletion */}
          <Card className="border-slate-200" data-testid="data-delete-card">
            <CardContent className="pt-6 pb-5 text-center space-y-3">
              <Trash2 className="w-8 h-8 text-red-500 mx-auto" />
              <h3 className="font-bold text-slate-900">Account Deletion</h3>
              <p className="text-xs text-slate-500">Request permanent deletion of your account and data within 30 days.</p>
              {!confirmDelete ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmDelete(true)}
                  disabled={hasPending('delete')}
                  className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                  data-testid="request-delete-btn"
                >
                  {hasPending('delete') ? 'Deletion Pending' : 'Request Deletion'}
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-red-500 font-medium">This action is irreversible. Are you sure?</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      size="sm"
                      onClick={() => submit('delete')}
                      disabled={submitting === 'delete'}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs"
                      data-testid="confirm-delete-btn"
                    >
                      {submitting === 'delete' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Confirm Delete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} className="text-xs">Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Request History */}
        {!loading && requests.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-slate-700 mb-3">Request History</h2>
            <div className="space-y-2" data-testid="request-history">
              {requests.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200">
                  <div className="flex items-center gap-2">
                    {r.type === 'export' ? <Download className="w-4 h-4 text-blue-500" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                    <span className="text-sm font-medium text-slate-700 capitalize">{r.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={r.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}>
                      {r.status === 'completed' ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                      {r.status}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{timeAgo(r.requested_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 text-center text-xs text-slate-400">
          <p>Requests are processed within 30 days in accordance with UK GDPR.</p>
          <p className="mt-1">
            Questions? Contact <a href="mailto:privacy@bidsentry.io" className="text-orange-500 hover:text-orange-600">privacy@bidsentry.io</a>
          </p>
        </div>
      </main>
    </div>
  );
}
